import { Group } from '@mantine/core';
import { WorkflowConfig } from '@/app/workflows/actions/interfaces';
import { useFormContext } from '../../../contexts/formContext';
import { OptionalSetting } from '../../../components/OptionalSetting';
import { TexturesSelect } from '../atoms/TexturesSelect';
import { CustomisedTexturesSelection } from '../atoms/CustomisedTexturesSelection';

const defaultValue = 2048;

const isCustom = (textures: NonNullable<WorkflowConfig['limits']>['textures']) => {
  const values = Object.values(textures ?? {});
  return !values.every((value) => value === values[0]);
};

export function TexturesSetting() {
  const form = useFormContext();

  const isSettingEnabled = Boolean(form.getValues().limits?.textures);
  const baseValue = form.getValues().limits?.textures?.baseColor ?? defaultValue;
  const value = isCustom(form.getValues().limits?.textures) ? 'custom' : baseValue;

  const onEnableChange = (enabled: boolean) => {
    if (enabled) {
      form.setValues((prev) => ({
        limits: {
          ...prev.limits,
          textures: {
            normal: defaultValue,
            orm: defaultValue,
            emissive: defaultValue,
            baseColor: defaultValue,
          },
        },
      }));
    } else {
      form.setValues((prev) => ({
        limits: { ...prev.limits, textures: undefined },
      }));
    }
  };

  const onValueChange = (value: string | null) => {
    if (!value) {
      return;
    }

    const parsed = parseInt(value);

    form.setValues((prev) => ({
      limits: {
        ...prev.limits,
        textures: {
          baseColor: parsed,
          orm: parsed,
          emissive: parsed,
          normal: parsed,
        },
      },
    }));
  };

  const onCustomisedValuesChanged = (newValue: Partial<NonNullable<WorkflowConfig['limits']>['textures']>) => {
    form.setValues((prev) => ({
      limits: {
        ...prev.limits,
        textures: { ...prev.limits?.textures!, ...newValue },
      },
    }));
  };

  return (
    <OptionalSetting isEnabled={isSettingEnabled} onChanged={onEnableChange} title='Texture Size'>
      <Group gap='xs'>
        <TexturesSelect value={value.toString()} onChange={onValueChange} />
      </Group>
      <CustomisedTexturesSelection onChanged={onCustomisedValuesChanged} values={form.values?.limits?.textures} />
    </OptionalSetting>
  );
}
